import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutList, Package, CalendarRange, Wrench, Users, Smartphone, RotateCcw, ArrowRight } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { useDeliveries, useCustomers, useServiceCases } from "@/lib/queries";
import { SERVICE_STATUS_LABELS } from "@/lib/format";
import { PhaseBadge, StateChip, toneForServiceStatus } from "@/components/common/Badges";

const PAGES = [
  { to: "/oversikt", label: "Oversikt", icon: LayoutList },
  { to: "/leveranser", label: "Leveranser", icon: Package },
  { to: "/plan", label: "Plan", icon: CalendarRange },
  { to: "/service", label: "Service", icon: Wrench },
  { to: "/kunder", label: "Kunder", icon: Users },
  { to: "/montor", label: "Montørvisning", icon: Smartphone },
];

/** Global search / navigation. Opens with Ctrl/Cmd+K. Only searches data already in the demo session. */
export const CommandPalette = ({ open, onOpenChange, onReset }) => {
  const navigate = useNavigate();
  const { data: deliveries } = useDeliveries();
  const { data: customers } = useCustomers();
  const { data: service } = useServiceCases("alle");

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Søk etter leveranse, kunde, serviceoppdrag eller side…" data-testid="command-palette-input" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>Ingen treff.</CommandEmpty>
        <CommandGroup heading="Leveranser">
          {(deliveries || []).map((d) => (
            <CommandItem key={d.id} value={`${d.number} ${d.project_name} ${d.customer_name} ${d.stair_summary}`} onSelect={() => go(`/leveranser/${d.id}`)} data-testid={`cmd-delivery-${d.number}`}>
              <Package className="text-muted-foreground" />
              <span className="truncate font-medium">{d.project_name}</span>
              <span className="t-meta tabular">{d.number}</span>
              <span className="hidden truncate t-meta sm:inline">{d.customer_name}</span>
              <span className="ml-auto"><PhaseBadge phase={d.phase} /></span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Kunder">
          {(customers || []).map((c) => (
            <CommandItem key={c.id} value={`kunde ${c.name} ${c.org_no || ""}`} onSelect={() => go(`/kunder/${c.id}`)} data-testid={`cmd-customer-${c.id}`}>
              <Users className="text-muted-foreground" />
              <span className="font-medium">{c.name}</span>
              <span className="ml-auto t-meta tabular">{c.active_deliveries} aktive</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Serviceoppdrag">
          {(service || []).map((s) => (
            <CommandItem key={s.id} value={`service ${s.number} ${s.title} ${s.project_name}`} onSelect={() => go(`/service/${s.id}`)} data-testid={`cmd-service-${s.number}`}>
              <Wrench className="text-muted-foreground" />
              <span className="truncate font-medium">{s.title}</span>
              <span className="t-meta tabular">{s.number}</span>
              <span className="ml-auto"><StateChip tone={toneForServiceStatus(s.status)}>{SERVICE_STATUS_LABELS[s.status]}</StateChip></span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Gå til">
          {PAGES.map(({ to, label, icon: Icon }) => (
            <CommandItem key={to} value={`side ${label}`} onSelect={() => go(to)} data-testid={`cmd-page-${to.replace("/", "")}`}>
              <Icon className="text-muted-foreground" /> {label}
              <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
            </CommandItem>
          ))}
          {onReset && (
            <CommandItem value="tilbakestill eksempeldata reset" onSelect={() => { onOpenChange(false); onReset(); }} data-testid="cmd-reset">
              <RotateCcw className="text-muted-foreground" /> Tilbakestill eksempeldata…
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-between border-t px-3 py-2 text-[12px] text-muted-foreground">
        <span>Søker kun i denne demoøkten</span>
        <span className="inline-flex items-center gap-1"><kbd className="kbd">↑↓</kbd> velg <kbd className="kbd">↵</kbd> åpne <kbd className="kbd">esc</kbd> lukk</span>
      </div>
    </CommandDialog>
  );
};

/** Keyboard shortcut hook for the palette. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const isMac = useMemo(() => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform), []);
  return { open, setOpen, shortcutLabel: isMac ? "⌘K" : "Ctrl K" };
}
